import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import {
  createCommandAsyncThunk,
  createQueryAsyncThunk,
} from "../../app/createApiAsyncThunk";
import { RootState } from "../../app/store";
import { EventPayload } from "../../common/types/Event";
import { Hits, Paginated, Relation } from "../../common/types/Paginated";
import {
  Attachment,
  EditEntry,
  Entry,
  EntryApprovalStateReset,
  EntryFilters,
  LeaveAttachmentDownloadUrl,
  LeaveAttachmentDownloadUrlQuery,
  LeaveAttachmentUploadUrl,
  LeaveAttachmentUploadUrlQuery,
  LeaveEntry,
  LeaveFromTo,
  LeaveMap,
  PostDeleteEntry,
  PostEditEntry,
  PostModifyAbsence,
  RemoveLeaveAttachment,
  UpdatableEntry,
  WrkEntAddEvent,
  addEntry,
  approveEntries,
  confirmEntriesRejected,
  deleteEntry,
  getAbsencesById,
  getEntryById,
  getLeaveEntryById,
  leaveAttachmentDownloadUrl,
  leaveAttachmentUploadUrl,
  listEntries,
  modifyAbsence,
  postEditEntry,
  removeLeaveAttachment,
  resetEntryApprovalStateCmd,
  uploadLeaveAttachmentToS3,
} from "./entriesAPI";
import { ABSENCE_TYPES_REQUIRING_ATTACHMENT } from "./Entry/Shared/functions";
import { AddEntry } from "./types/commands";
import { WorkEntryAdded } from "./types/events";

export type SliceState = {
  entries: {
    ids: string[];
    entities: Record<string, Entry>;
    cursor: string | null;
    filters: EntryFilters;
    status: "idle" | "pending" | "succeeded" | "failed";
    error?: string;
    entry: EditEntry | null;
    hits?: Hits;
  };
  absenceEntries: {
    ids: string[];
    entities: Record<string, Entry>;
    cursor: string | null;
    filters: EntryFilters;
    status: "idle" | "pending" | "succeeded" | "failed";
    error?: string;
    entry: EditEntry | null;
    hits?: Hits;
  };
};

const entriesAdapter = createEntityAdapter<Entry>();

const absenceEntriesAdapter = createEntityAdapter<Entry>();

export const postResetEntryApprovalState = createCommandAsyncThunk<
  UpdatableEntry[],
  EntryApprovalStateReset
>("entries/resetEntryApprovalState", resetEntryApprovalStateCmd);

export const postConvertToAbsence = createAsyncThunk<
  any,
  PostEditEntry,
  { state: RootState }
>("working/cmd/editEntry", async (entry, { requestId }): Promise<any> => {
  return await postEditEntry(entry, { requestId });
});

export const fetchEntryById = createAsyncThunk<
  EditEntry,
  string,
  { state: RootState }
>("entries/fetchEntryById", async (entryId: string, { requestId }) => {
  const res = await getEntryById(entryId, { requestId });
  return res.data as EditEntry;
});

export const fetchEntriesByWrkentAddIds = createAsyncThunk<
  { entry: Entry; splitFrom: string }[],
  WrkEntAddEvent[],
  { state: RootState }
>(
  "entries/fetchEntriesByWrkentAddIds",
  async (wrkEntAddEvents: WrkEntAddEvent[], { requestId, rejectWithValue }) => {
    const maxRetries = 50;
    let attempts = 0;

    /* Get the last event id, because when it's found from backend,
     all other events are also there */
    const lastEventId = wrkEntAddEvents[wrkEntAddEvents.length - 1].id;

    while (attempts < maxRetries) {
      try {
        // Fetch the entry from last event id
        const firstResponse = (await getEntryById(lastEventId, {
          requestId,
        })) as { data: Entry };
        // If data is found, fetch all other entries
        if (firstResponse.data) {
          const otherResponses = await Promise.all(
            wrkEntAddEvents
              .filter((event) => event.id !== lastEventId)
              .map(
                (event) =>
                  getEntryById(event.id, { requestId }) as Promise<{
                    data: Entry;
                  }>,
              ),
          );

          // Combine all data with splitFrom property
          const allEventsData = [
            {
              entry: firstResponse.data,
              splitFrom:
                wrkEntAddEvents.find((event) => event.id === lastEventId)
                  ?.payload.splitFrom ?? "",
            },
            ...otherResponses.map((response) => ({
              entry: response.data,
              splitFrom:
                wrkEntAddEvents.find((event) => event.id === response.data.id)
                  ?.payload.splitFrom ?? "",
            })),
          ];
          // Return all data, which is handled in extraReducers
          return allEventsData;
        }
      } catch (error) {
        if (attempts === maxRetries - 1) {
          return rejectWithValue(
            "Failed to fetch entry after maximum retries.",
          );
        }
      }

      attempts++;
      await new Promise((resolve) => setTimeout(resolve, 300));
    }

    return rejectWithValue("Data not available after retries.");
  },
);

export const fetchLeaveEntryById = createAsyncThunk<
  LeaveEntry,
  string,
  { state: RootState }
>("entries/fetchLeaveEntryById", async (leaveId: string, { requestId }) => {
  const res = await getLeaveEntryById(leaveId, { requestId });
  return res.data;
});

export const fetchAbsencesById = createAsyncThunk<
  LeaveMap,
  LeaveFromTo,
  { state: RootState }
>(
  "entries/fetchAbsencesById",
  async (absenceFromTo: LeaveFromTo, { requestId }) => {
    const res = await getAbsencesById(absenceFromTo, { requestId });

    // Linked array of types
    const linkedTypes = [
      "SICK_LEAVE",
      "SICK_LEAVE_SELF_REPORTED",
      "UNSUDDEN_SICK_LEAVE",
    ];
    const filteredData = res.data.filter((absence) => {
      // Check if both the absence and absenceFromTo types are in the linkedTypes array
      return (
        absence.type === absenceFromTo.type ||
        (linkedTypes.includes(absenceFromTo.type) &&
          linkedTypes.includes(absence.type))
      );
    });


    const map: LeaveMap = { order: [], chains: {} } ;//new Map<string, ContinuedLeave[]>();

    for (const leave of filteredData) {
        const rootId = leave.continuationOf ?? leave.id;
        if (!map.order.includes(rootId)) {
          map.order.push(rootId);
          map.chains[rootId] = [];
        }
      map.chains[rootId]!.push(leave);
    }

    // Now sort each chain in reverse chronological order
    Object.keys(map.chains).forEach((key) => {
        map.chains[key].sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
    })

    map.order.sort((aId, bId) => {
        // Optional: Sort roots by most recent leave in each chain
        const aLeaves = map.chains[aId];
        const bLeaves = map.chains[bId];
        return new Date(bLeaves[0].startDate).getTime() - new Date(aLeaves[0].startDate).getTime();
    });
    return map;
    
  },
);

export const editEntry = createAsyncThunk<
  any,
  PostEditEntry,
  { state: RootState }
>("entries/editEntry", async (entry, { requestId }): Promise<any> => {
  return await postEditEntry(entry, { requestId });
});

export const fetchEntries = createAsyncThunk<
  Paginated<Entry[]>,
  EntryFilters,
  { state: RootState }
>("entries/fetchEntries", async (filters: EntryFilters, { requestId }) => {
  return await listEntries(filters, {
    requestId,
  });
});

export const fetchEntriesAbsence = createAsyncThunk<
  Paginated<Entry[]>,
  EntryFilters,
  { state: RootState }
>(
  "entries/fetchEntriesAbsence",
  async (filters: EntryFilters, { requestId }) => {
    // If filters have approval property with 'all' value, remove property as in this case query should not have property at all
    let modifiedFilters = filters;
    if (modifiedFilters?.approval === "all") {
      delete modifiedFilters.approval;
    }
    const response = await listEntries(modifiedFilters, {
      requestId,
    });
    return response;
  },
);

export const fetchMoreEntries = createAsyncThunk<
  Paginated<Entry[]>,
  {},
  { state: RootState }
>("entries/fetchMoreEntries", async (_ = {}, { requestId, getState }) => {
  const state = getState().entries;
  const cursor = state.entries.cursor as string;
  const currentFilters = state.entries.filters;
  const response = await listEntries(
    { cursor, ...currentFilters },
    {
      requestId,
    },
  );
  return response;
});

export const postApproveEntries = createAsyncThunk<
  any, // TODO: add type
  UpdatableEntry[],
  { state: RootState }
>("entries/approveEntries", async (updatableEntries, { requestId }) => {
  const response = await approveEntries(updatableEntries, {
    requestId,
  });
  return response;
});

export const postConfirmEntriesRejected = createAsyncThunk<
  any, // TODO: add type
  UpdatableEntry[],
  { state: RootState }
>("entries/confirmEntriesRejected", async (updatableEntries, { requestId }) => {
  const response = await confirmEntriesRejected(updatableEntries, {
    requestId,
  });
  return response;
});

export const postModifyAbsence = createAsyncThunk<
  any, // TODO: add type
  PostModifyAbsence,
  { state: RootState }
>("entries/modifyAbsence", async (postLeaveEntity, { requestId }) => {
  const response = await modifyAbsence(postLeaveEntity, {
    requestId,
  });
  return response.data;
});

export const postDeleteEntry = createAsyncThunk<
  any, // TODO: add type
  PostDeleteEntry,
  { state: RootState }
>("entries/deleteEntry", async (postDeleteEntry, { requestId }) => {
  const response = await deleteEntry(postDeleteEntry, {
    requestId,
  });
  return response.data;
});

export const getLeaveAttachmentDownloadUrl = createQueryAsyncThunk<
  LeaveAttachmentDownloadUrl,
  LeaveAttachmentDownloadUrlQuery
>("entries/leaveAttachmentDownloadUrl", leaveAttachmentDownloadUrl);

export const doRemoveLeaveAttachment = createAsyncThunk<
  any,
  RemoveLeaveAttachment,
  { state: RootState }
>(
  "entries/doRemoveLeaveAttachment",
  async (command: RemoveLeaveAttachment, { requestId }): Promise<any> =>
    await removeLeaveAttachment(command, { requestId }),
);

export const getLeaveAttachmentUploadUrl = createQueryAsyncThunk<
  LeaveAttachmentUploadUrl,
  LeaveAttachmentUploadUrlQuery
>("entries/getLeaveAttachmentUploadUrl", leaveAttachmentUploadUrl);

export const doUploadLeaveAttachmentToS3 = createAsyncThunk<
  void,
  { url: string; file: File },
  { state: RootState }
>(
  "entries/doUploadLeaveAttachmentToS3",
  async ({ url, file }): Promise<void> =>
    await uploadLeaveAttachmentToS3(url, file),
);

export const addNewEntry = createAsyncThunk<
  EventPayload<WorkEntryAdded>[],
  AddEntry,
  { state: RootState }
>(
  "entries/addNewEntry",
  async (
    command: AddEntry,
    { requestId },
  ): Promise<EventPayload<WorkEntryAdded>[]> =>
    await addEntry(command, { requestId }),
);

export const addNewEntries = createAsyncThunk<
  EventPayload<WorkEntryAdded>[][],
  AddEntry[],
  { state: RootState }
>(
  "entries/addNewEntries",
  async (
    entries: AddEntry[],
    { requestId },
  ): Promise<EventPayload<WorkEntryAdded>[][]> => {
    // TODO send requests in batches instead of at once
    return await Promise.all(
      entries.map((entry) => addEntry(entry, { requestId })),
    );
  },
);

const handleAbsenceFulfilled = (state: any, action: any) => {
  const events = action.payload;
  const deletions = []; // Track entries for deletion

  for (const entry of events) {
    let entryChanges = {};

    if (entry.name === "wrkentChg") {
      const newType = entry.payload.details.type;
      entryChanges = {
        id: entry.id,
        start: entry.payload.details.start,
        end: entry.payload.details.end,
        version: entry.version,
        comment: entry.comment,
        lunchBreak: entry.payload.details.lunchBreak,
        leaveId: entry.payload.details.leaveId,
        type: newType,
        customerCostCenterId: entry.payload.details.customerCostCenterId,
        leaveAttachmentRequired: ABSENCE_TYPES_REQUIRING_ATTACHMENT.includes(newType),
      };
    } else if (entry.name === "wrkentSta") {
      entryChanges = {
        version: entry.version,
        approvalState: entry.payload.newState,
      };
    } else if (entry.name === "wrkentDel") {
      // Track for deletion and skip to next event
      deletions.push(entry.id);
      continue;
    }

    if (Object.keys(entryChanges).length > 0) {
      // Apply updates for entries and absenceEntries
      entriesAdapter.updateOne(state.entries, {
        id: entry.id,
        changes: entryChanges,
      });
      absenceEntriesAdapter.updateOne(state.absenceEntries, {
        id: entry.id,
        changes: entryChanges,
      });
    }
  }

  // Process deletions at the end
  for (const id of deletions) {
    entriesAdapter.removeOne(state.entries, id);
    absenceEntriesAdapter.removeOne(state.absenceEntries, id);
  }

  state.entries.status = "succeeded";
  state.entries.error = "";
  state.absenceEntries.status = "succeeded";
  state.absenceEntries.error = "";
};

export const entriesSlice = createSlice({
  name: "entries",
  initialState: {
    entries: entriesAdapter.getInitialState({
      cursor: null,
      filters: {},
      status: "idle",
      error: "",
      entry: null,
      hits: { value: 0, relation: Relation.DEFAULT },
    }),
    absenceEntries: absenceEntriesAdapter.getInitialState({
      cursor: null,
      filters: {},
      status: "idle",
      error: "",
      entry: null,
    }),
  } as SliceState,
  reducers: {
    clearCursor: (state) => {
      state.entries.cursor = null;
      state.absenceEntries.cursor = null;
    },
    clearAbsenceEntries: (state: any) => {
      state.absenceEntries = absenceEntriesAdapter.getInitialState();
    },
    updateEntriesLeaveAttachment: (
      state,
      action: PayloadAction<{ id: string; leaveAttachment: { attachments?: Attachment[] } | undefined }[]>,
    ) => {
      const updates = action.payload.map(({ id, leaveAttachment }) => ({
        id,
        changes: { leaveAttachment },
      }));
      entriesAdapter.updateMany(state.entries, updates);
      absenceEntriesAdapter.updateMany(state.absenceEntries, updates);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEntryById.pending, (state) => {
        state.entries.status = "pending";
      })
      .addCase(fetchEntryById.fulfilled, (state, action) => {
        state.entries.entry = action.payload;
        state.entries.status = "succeeded";
        state.entries.error = "";
      })
      .addCase(fetchEntryById.rejected, (state, action) => {
        state.entries.error = action.error.message;
        state.entries.status = "failed";
      })
      .addCase(editEntry.fulfilled, (state, action) => {
        const events = action.payload;

        for (const entry of events) {
          let entryChanges = {};
          // If event is response of updating entry
          if (entry.name === "wrkentChg") {
            entryChanges = {
              id: entry.id,
              start: entry.payload.details.start,
              end: entry.payload.details.end,
              isBillable: entry.payload.details.isBillable,
              version: entry.version,
              comment: entry.comment,
              lunchBreak: entry.payload.details.lunchBreak,
            };
            // If event is related to approval change
          } else if (entry.name === "wrkentSta") {
            entryChanges = {
              version: entry.version,
              approvalState: entry.payload.newState,
            };
          } else {
            // Default action for other entries
          }
          // If there werechanges done in events, run update
          if (Object.keys(entryChanges).length > 0) {
            entriesAdapter.updateOne(state.entries, {
              id: entry.id,
              changes: entryChanges,
            });

            absenceEntriesAdapter.updateOne(state.absenceEntries, {
              id: entry.id,
              changes: entryChanges,
            });
          }
        }
        state.entries.status = "succeeded";
        state.absenceEntries.status = "succeeded";
      })
      .addCase(editEntry.pending, (state) => {
        state.entries.status = "pending";
      })
      .addCase(editEntry.rejected, (state, action) => {
        state.entries.error = action.error.message;
        state.entries.status = "failed";
      })
      .addCase(addNewEntry.fulfilled, (state) => {
        state.entries.status = "succeeded";
        state.absenceEntries.status = "succeeded";
      })
      .addCase(addNewEntry.pending, (state) => {
        state.entries.status = "pending";
      })
      .addCase(addNewEntry.rejected, (state, action) => {
        state.entries.error = action.error.message;
        state.entries.status = "failed";
      })
      .addCase(fetchEntries.pending, (state) => {
        state.entries.status = "pending";
      })
      .addCase(fetchEntries.fulfilled, (state, action) => {
        state.entries.filters = action.meta.arg;
        entriesAdapter.setAll(state.entries, action.payload.data);
        state.entries.hits = {
          value: action.payload.hits?.value || 0,
          relation: action.payload.hits?.relation || Relation.DEFAULT,
        };
        state.entries.cursor = action.payload.cursor;
        state.entries.status = "succeeded";
        state.entries.error = "";
      })
      .addCase(fetchEntries.rejected, (state, action) => {
        state.entries.error = action.error.message;
        state.entries.status = "failed";
      })
      .addCase(fetchEntriesAbsence.pending, (state) => {
        state.absenceEntries.status = "pending";
      })
      .addCase(fetchEntriesAbsence.fulfilled, (state, action) => {
        state.absenceEntries.filters = action.meta.arg;

        // Update existing entries in the 'entries' list
        entriesAdapter.upsertMany(state.entries, action.payload.data);

        // Replace entire contents of 'absenceEntries' list
        absenceEntriesAdapter.setAll(state.absenceEntries, action.payload.data);

        state.absenceEntries.cursor = action.payload.cursor;
        state.absenceEntries.status = "succeeded";
        state.absenceEntries.error = "";
      })
      .addCase(fetchEntriesAbsence.rejected, (state, action) => {
        state.absenceEntries.error = action.error.message;
        state.absenceEntries.status = "failed";
      })
      .addCase(fetchEntriesByWrkentAddIds.fulfilled, (state, action) => {
        // Get all events from the response
        const events = action.payload;

        // For each event, update the entries state
        events.forEach(({ entry, splitFrom }) => {
          if (splitFrom) {
            // If splitFrom is provided, try to find its index
            const index = state.entries.ids.findIndex((id) => id === splitFrom);

            if (index !== -1) {
              const splitFromEntry = state.entries.entities[splitFrom];

              if (splitFromEntry) {
                // Compare start dates to decide whether to insert before or after splitFrom
                if (entry.start < splitFromEntry.start) {
                  state.entries.ids.splice(index, 0, entry.id); // Insert before splitFrom
                } else {
                  state.entries.ids.splice(index + 1, 0, entry.id); // Insert after splitFrom
                }
              }
            }
          } else {
            // If splitFrom is not provided (i.e., it's a new entry), insert based on start date
            let inserted = false;

            // Iterate through existing ids and compare start dates
            for (let i = 0; i < state.entries.ids.length; i++) {
              const existingEntry =
                state.entries.entities[state.entries.ids[i]];
              if (existingEntry && entry.start < existingEntry.start) {
                state.entries.ids.splice(i, 0, entry.id);
                inserted = true;
                break;
              }
            }

            // If no earlier entry was found, add to the end
            if (!inserted) {
              state.entries.ids.push(entry.id);
            }
          }

          // Always update the entities with the new entry data
          state.entries.entities[entry.id] = entry;
        });

        // this uses fetchEntryById so we need to update the
        state.entries.hits = {
          value: state.entries.ids.length,
          // relation is assumed to be EQ
          relation: Relation.EQ,
        };
        state.entries.status = "succeeded";
        state.entries.error = "";
      })
      .addCase(fetchMoreEntries.pending, (state) => {
        state.entries.status = "pending";
      })
      .addCase(fetchMoreEntries.fulfilled, (state, action) => {
        entriesAdapter.upsertMany(state.entries, action.payload.data);
        state.entries.cursor = action.payload.cursor;
        state.entries.status = "succeeded";
        state.entries.error = "";
      })
      .addCase(fetchMoreEntries.rejected, (state, action) => {
        state.entries.error = action.error.message;
        state.entries.status = "failed";
      })
      .addCase(postApproveEntries.pending, (state) => {
        state.entries.status = "pending";
        state.absenceEntries.status = "pending";
      })
      .addCase(postApproveEntries.fulfilled, (state, action) => {
        const payload = action.payload;

        // Update multiple entries
        const updatedEntries = payload.map((entry: any) => ({
          id: entry.id,
          changes: {
            version: entry.version,
            approvalState: entry.payload.newState,
          },
        }));

        // Update entries state in main view listing
        entriesAdapter.updateMany(state.entries, updatedEntries);

        // Update entries state in absence view listing
        absenceEntriesAdapter.updateMany(state.absenceEntries, updatedEntries);

        state.entries.status = "succeeded";
        state.entries.error = "";

        state.absenceEntries.status = "succeeded";
        state.absenceEntries.error = "";
      })
      .addCase(postApproveEntries.rejected, (state, action) => {
        state.entries.error = action.error.message;
        state.entries.status = "failed";
      })
      .addCase(postResetEntryApprovalState.fulfilled, (state, action) => {
        const updates = action.payload.map((entry) => ({
          id: entry.id,
          changes: {
            version: entry.version,
            approvalState: entry.payload.newState,
          },
        }));
        entriesAdapter.updateMany(state.entries, updates);
        absenceEntriesAdapter.updateMany(state.absenceEntries, updates);
      })
      .addCase(postDeleteEntry.pending, (state) => {
        state.entries.status = "pending";
      })
      .addCase(postDeleteEntry.fulfilled, (state, action) => {
        const events = action.payload;
        // Find wrkentDel event and delete entry from state
        const entryId = events.find(
          (event: any) => event.name === "wrkentDel",
        )?.id;
        entriesAdapter.removeOne(state.entries, entryId);
        absenceEntriesAdapter.removeOne(state.absenceEntries, entryId);
        state.entries.status = "succeeded";
        state.entries.error = "";
      })
      .addCase(postDeleteEntry.rejected, (state, action) => {
        state.entries.error = action.error.message;
        state.entries.status = "failed";
      })
      .addCase(postConfirmEntriesRejected.pending, (state) => {
        state.entries.status = "pending";
      })
      .addCase(postConfirmEntriesRejected.fulfilled, (state, action) => {
        const payload = action.payload;

        // Update multiple entries
        const updatedEntries = payload.map((entry: any) => ({
          id: entry.id,
          changes: {
            version: entry.version,
            approvalState: entry.payload.newState,
          },
        }));

        entriesAdapter.updateMany(state.entries, updatedEntries);

        state.entries.status = "succeeded";
        state.entries.error = "";
      })
      .addCase(postConfirmEntriesRejected.rejected, (state, action) => {
        state.entries.error = action.error.message;
        state.entries.status = "failed";
      })
      .addCase(doRemoveLeaveAttachment.pending, (state) => {
        state.entries.status = "pending";
      })
      .addCase(doRemoveLeaveAttachment.fulfilled, (state, action) => {
        state.entries.status = "succeeded";
        state.entries.error = "";
      })
      .addCase(doRemoveLeaveAttachment.rejected, (state, action) => {
        state.entries.error = action.error.message;
        state.entries.status = "failed";
      })
      .addCase(getLeaveAttachmentDownloadUrl.pending, (state) => {
        state.entries.status = "pending";
      })
      .addCase(getLeaveAttachmentDownloadUrl.fulfilled, (state, action) => {
        state.entries.status = "succeeded";
        state.entries.error = "";
      })
      .addCase(getLeaveAttachmentDownloadUrl.rejected, (state, action) => {
        state.entries.error = action.error.message;
        state.entries.status = "failed";
      })
      .addCase(getLeaveAttachmentUploadUrl.pending, (state) => {
        state.entries.status = "pending";
      })
      .addCase(getLeaveAttachmentUploadUrl.fulfilled, (state) => {
        state.entries.status = "succeeded";
        state.entries.error = "";
      })
      .addCase(getLeaveAttachmentUploadUrl.rejected, (state, action) => {
        state.entries.error = action.error.message;
        state.entries.status = "failed";
      })
      .addCase(doUploadLeaveAttachmentToS3.pending, (state) => {
        state.entries.status = "pending";
      })
      .addCase(doUploadLeaveAttachmentToS3.fulfilled, (state) => {
        state.entries.status = "succeeded";
        state.entries.error = "";
      })
      .addCase(doUploadLeaveAttachmentToS3.rejected, (state, action) => {
        state.entries.error = action.error.message;
        state.entries.status = "failed";
      })
      .addCase(postModifyAbsence.rejected, (state, action) => {
        state.entries.error = action.error.message;
        state.entries.status = "failed";
      })
      .addCase(postModifyAbsence.fulfilled, handleAbsenceFulfilled)
      .addCase(addNewEntries.pending, (state) => {
        state.entries.status = "pending";
      })
      .addCase(addNewEntries.fulfilled, (state) => {
        state.entries.status = "succeeded";
      })
      .addCase(addNewEntries.rejected, (state, action) => {
        state.entries.status = "failed";
        state.entries.error = action.error.message;
      });
  },
});

export const { clearCursor, clearAbsenceEntries, updateEntriesLeaveAttachment } = entriesSlice.actions;

export const { selectAll: selectAllEntries, selectById: selectEntryById } =
  entriesAdapter.getSelectors<RootState>((state) => state.entries.entries);

export const {
  selectAll: selectAllAbsenceEntries,
  selectById: selectAbsenceEntryById,
} = absenceEntriesAdapter.getSelectors<RootState>(
  (state) => state.entries.absenceEntries,
);

export default entriesSlice.reducer;
